import { reportError } from "@utils/errorReporting";
import { fetchWithTimeout } from "@utils/fetchWithTimeout";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

const freshchatInit = ({ hideWidget = false } = {}) => {
	const reportFreshchatError = (
		error,
		action,
		metadata: Record<string, unknown> = {},
	) => {
		reportError(error, {
			route: window.location.pathname,
			component: "useFreshchat",
			action,
			metadata,
		});
	};

	dayjs.extend(utc);
	dayjs.extend(timezone);
	dayjs.extend(isBetween);

	const PathToHideWidget =
		window.location.href.includes("sustainability-report") ||
		window.location.href.includes("buy") ||
		window.location.href.includes("identify") ||
		window.location.href.includes("retail");
	const shouldHideWidget = PathToHideWidget || hideWidget;
	const AddTagToPage = window.location.href.includes("care-team-fresh");

	try {
		window.fcWidget.init({
			config: {
				eagerLoad: true,
				headerProperty: {
					hideChatButton: shouldHideWidget,
				},
			},
			token: "4a1fc343-5663-49fc-bb28-7a721656260d",
			host: "https://wchat.au.freshchat.com",
			tags: AddTagToPage ? ["Botversiontest"] : ["FelixBot"],
		});
	} catch (error) {
		reportFreshchatError(error, "initialize_widget");
		return false;
	}

	const authenticateUser = async (userData) => {
		const authenticateCB = async (uuid) => {
			try {
				const url = `${process.env.NEXT_PUBLIC_AWS_BASE_URL}/token/${uuid}`;
				const response = await fetchWithTimeout(url);
				const resultInJson = await response.json();
				window.fcWidget.authenticate(resultInJson.data.token);
			} catch (error) {
				reportFreshchatError(error, "authenticate_user", {
					hasUuid: Boolean(uuid),
				});
			}
		};

		if (userData?.freshchat_uuid) {
			await authenticateCB(userData.freshchat_uuid);
		} else {
			try {
				const resp = await window.fcWidget.user.getUUID();
				const uuid = resp?.data?.uuid;
				await authenticateCB(uuid);
			} catch (error) {
				reportFreshchatError(error, "get_uuid");
			}
		}
	};

	window.fcWidget.on("frame:statechange", (data) => {
		if (
			data.success === false &&
			data.data.frameState === "not_authenticated"
		) {
			authenticateUser(data);
		}
	});

	window.fcWidget.on("user:statechange", (data) => {
		if (data.success) {
			const userData = data.data;
			if (userData) {
				if (
					userData.userState === "not_loaded" ||
					userData.userState === "unloaded" ||
					userData.userState === "not_created" ||
					userData.userState === "not_authenticated"
				) {
					authenticateUser(userData);
				}
			}
		} else {
			const userData = data.data;
			if (userData) {
				if (
					userData.userState === "not_loaded" ||
					userData.userState === "unloaded" ||
					userData.userState === "not_created" ||
					userData.userState === "not_authenticated"
				) {
					authenticateUser(userData);
				}
			}
		}
	});

	window.fcWidget.on("widget:opened", () => {
		window?.dataLayer?.push({
			event: "liveChatOpen",
		});
	});

	return true;
};

export const useFreshchat = ({ hideWidget = false } = {}) => {
	const [initialized, setInitialized] = useState(false);

	const pathname = usePathname();

	useEffect(() => {
		const checkAndInitFreshchat = async () => {
			const maxRetries = 10;
			let retries = 0;
			const interval = 500; // Check every 500ms

			const intervalId = setInterval(() => {
				if (typeof window !== "undefined" && window.fcWidget && !initialized) {
					const result = freshchatInit({ hideWidget });
					setInitialized(result);
					clearInterval(intervalId);
				} else if (retries >= maxRetries) {
					reportError(
						new Error("Freshchat widget unavailable after retry budget"),
						{
							route: pathname || window.location.pathname,
							component: "useFreshchat",
							action: "wait_for_widget",
							metadata: {
								maxRetries,
								interval,
							},
						},
					);
					clearInterval(intervalId);
				} else {
					retries++;
				}
			}, interval);
		};

		checkAndInitFreshchat();
	}, [hideWidget, initialized, pathname]);

	useEffect(() => {
		if (typeof window !== "undefined" && window.fcWidget && initialized) {
			if (pathname?.includes("buy") || hideWidget) {
				window?.fcWidget?.hide();
				return;
			}

			window?.fcWidget?.show();
		}
	}, [hideWidget, initialized, pathname]);
};
